import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';

function SpecAgent() {
  const navigate = useNavigate();
  const [specs, setSpecs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('fs');
  const [refineInput, setRefineInput] = useState('');
  const [refining, setRefining] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMail, setShowMail] = useState(false);
  const [mailTo, setMailTo] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailSending, setMailSending] = useState(false);
  const [mailResult, setMailResult] = useState('');

  useEffect(() => { loadSpecs(); }, []);

  const loadSpecs = async () => {
    try {
      const res = await fetch(`${API}/spec/list`);
      const data = await res.json();
      setSpecs(data.specs || []);
    } catch (e) {}
  };

  const loadSpec = async (id) => {
    try {
      const res = await fetch(`${API}/spec/${id}`);
      const data = await res.json();
      setSelected(data);
      setActiveTab('fs');
    } catch (e) {}
  };

  const handleRefine = async () => {
    if (!refineInput.trim() || !selected) return;
    const spec = activeTab === 'fs' ? selected.fs_spec : selected.ts_spec;
    setRefining(true);
    try {
      const res = await fetch(`${API}/report/refine`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({spec, instruction: refineInput, spec_type: activeTab.toUpperCase()})
      });
      const data = await res.json();
      if (data.spec) {
        setSelected(prev => ({...prev, [activeTab === 'fs' ? 'fs_spec' : 'ts_spec']: data.spec}));
        setRefineInput('');
      }
    } catch (e) { alert('보정 실패'); }
    setRefining(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await fetch(`${API}/spec/${selected.id}`, {
        method: 'PUT', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({title: selected.title, fs_spec: selected.fs_spec, ts_spec: selected.ts_spec})
      });
      setMsg('저장 완료'); setTimeout(() => setMsg(''), 2000);
      loadSpecs();
    } catch (e) { setMsg('저장 실패'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await fetch(`${API}/spec/${id}`, {method: 'DELETE'});
    if (selected?.id === id) setSelected(null);
    loadSpecs();
  };

  const handleDownload = async (format) => {
    if (!selected) return;
    const spec = activeTab === 'fs' ? selected.fs_spec : selected.ts_spec;
    const label = selected.title + '_' + activeTab.toUpperCase();
    try {
      const res = await fetch(`${API}/prepare-download`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({answer: spec, question: label, format})
      });
      const prepData = await res.json(); if (prepData.file_id) { window.open(`${API}/get-download/${prepData.file_id}`, '_self'); return; }
    } catch (e) { alert('다운로드 실패'); }
  };

  const openMail = () => {
    if (!selected) return;
    setMailTo('');
    setMailSubject('[스펙정의서] ' + selected.title + ' (FS/TS)');
    setMailResult('');
    setShowMail(true);
  };

  const handleSendMail = async () => {
    if (!mailTo.trim()) return alert('수신자 이메일을 입력해주세요.');
    setMailSending(true);
    setMailResult('');
    try {
      const res = await fetch(`${API}/spec/send-mail`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          to: mailTo, subject: mailSubject, title: selected.title,
          fs_spec: selected.fs_spec, ts_spec: selected.ts_spec,
          doc_id: selected.doc_id || '', program: selected.program || '', module: selected.module || ''
        })
      });
      const data = await res.json();
      setMailResult(data.error ? 'error:' + data.error : 'success:' + data.message);
    } catch (e) { setMailResult('error:전송 실패'); }
    setMailSending(false);
  };

  const md = {
    h1: ({children}) => <h1 style={{marginTop:20,marginBottom:10,fontSize:20,color:'#1565c0',borderBottom:'2px solid #bbdefb',paddingBottom:6}}>{children}</h1>,
    h2: ({children}) => <h2 style={{marginTop:18,marginBottom:8,fontSize:17,color:'#1565c0',borderBottom:'1px solid #e3f2fd',paddingBottom:4}}>{children}</h2>,
    h3: ({children}) => <h3 style={{marginTop:16,marginBottom:6,fontSize:15,color:'#1976d2',borderLeft:'4px solid #1976d2',paddingLeft:10}}>{children}</h3>,
    strong: ({children}) => <strong style={{color:'#0d47a1'}}>{children}</strong>,
    p: ({children}) => <p style={{margin:'6px 0',lineHeight:1.7}}>{children}</p>,
    ul: ({children}) => <ul style={{paddingLeft:20,margin:'6px 0'}}>{children}</ul>,
    ol: ({children}) => <ol style={{paddingLeft:20,margin:'6px 0'}}>{children}</ol>,
    li: ({children}) => <li style={{marginBottom:3}}>{children}</li>,
    table: ({children}) => <div style={{overflowX:'auto',margin:'10px 0'}}><table style={{borderCollapse:'collapse',width:'100%',fontSize:13,border:'1px solid #bbdefb'}}>{children}</table></div>,
    thead: ({children}) => <thead style={{background:'#e3f2fd'}}>{children}</thead>,
    th: ({children}) => <th style={{border:'1px solid #90caf9',padding:'8px 10px',background:'#e3f2fd',textAlign:'left',fontWeight:'bold',color:'#0d47a1',whiteSpace:'nowrap'}}>{children}</th>,
    td: ({children}) => <td style={{border:'1px solid #bbdefb',padding:'7px 10px'}}>{children}</td>,
    code: ({children}) => <code style={{background:'#e3f2fd',padding:'2px 6px',borderRadius:4,fontSize:13}}>{children}</code>,
    pre: ({children}) => {
      const child = React.Children.toArray(children)[0];
      if (child?.props?.className === 'language-html') {
        const htmlCode = child.props.children;
        return (
          <div style={{margin:'12px 0'}}>
            <div style={{padding:'8px 14px',background:'#1565c0',color:'white',borderRadius:'8px 8px 0 0',fontSize:12,fontWeight:'bold',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span>화면 미리보기 (HTML)</span>
              <button onClick={() => {const w=window.open('','_blank','width=1000,height=600');w.document.write(htmlCode);w.document.close();}}
                style={{padding:'3px 10px',background:'rgba(255,255,255,0.2)',color:'white',border:'1px solid rgba(255,255,255,0.3)',borderRadius:4,cursor:'pointer',fontSize:11}}>
                새 창에서 보기
              </button>
            </div>
            <div style={{border:'1px solid #ccc',borderTop:'none',borderRadius:'0 0 8px 8px',overflow:'hidden',background:'white'}}>
              <div dangerouslySetInnerHTML={{__html: htmlCode}} style={{padding:0}} />
            </div>
          </div>
        );
      }
      return <pre style={{background:'#f5f5f5',padding:12,borderRadius:6,overflowX:'auto',fontSize:13}}>{children}</pre>;
    },
  };

  return (
    <div style={{minHeight:'100vh',background:'#f5f7fa'}}>
      <div style={{background:'linear-gradient(135deg,#0d47a1,#1565c0,#1976d2)',padding:'14px 20px',display:'flex',alignItems:'center',gap:16,boxShadow:'0 2px 12px rgba(21,101,192,0.3)'}}>
        <button onClick={() => navigate('/')} style={{background:'rgba(255,255,255,0.15)',border:'none',color:'white',width:36,height:36,borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div>
          <div style={{color:'white',fontSize:18,fontWeight:'bold'}}>스펙정의서</div>
          <div style={{color:'rgba(255,255,255,0.8)',fontSize:12}}>저장된 FS/TS 스펙 조회, AI 보정, 다운로드</div>
        </div>
      </div>

      <div style={{maxWidth:1200,margin:'24px auto',padding:'0 20px 40px',display:'grid',gridTemplateColumns:selected?'300px 1fr':'1fr',gap:20}}>
        {/* 목록 */}
        <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',padding:16,height:'fit-content',maxHeight:'calc(100vh - 120px)',overflowY:'auto'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <h3 style={{margin:0,fontSize:15,color:'#1565c0'}}>저장된 스펙 ({specs.length})</h3>
            <button onClick={loadSpecs} style={{padding:'4px 10px',background:'#e3f2fd',border:'1px solid #90caf9',borderRadius:4,cursor:'pointer',fontSize:12,color:'#1565c0'}}>새로고침</button>
          </div>
          {/* 검색 */}
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="제목, 테이블명, 문서ID, 프로그램명 검색..."
            style={{width:'100%',padding:'8px 10px',borderRadius:6,border:'1px solid #ddd',fontSize:12,marginBottom:12,boxSizing:'border-box'}} />

          {specs.length === 0 && <p style={{color:'#999',fontSize:13,textAlign:'center',padding:20}}>저장된 스펙이 없습니다.<br/>보고서 작성 에이전트에서 생성 후 저장하세요.</p>}
          {specs
            .filter(s => {
              if (!searchQuery.trim()) return true;
              const q = searchQuery.toLowerCase();
              return (s.title||'').toLowerCase().includes(q)
                || (s.tables||'').toLowerCase().includes(q)
                || (s.doc_id||'').toLowerCase().includes(q)
                || (s.program||'').toLowerCase().includes(q)
                || (s.module||'').toLowerCase().includes(q);
            })
            .map(s => (
            <div key={s.id} onClick={() => loadSpec(s.id)}
              style={{padding:'12px 14px',marginBottom:8,borderRadius:8,cursor:'pointer',
                border: selected?.id === s.id ? '2px solid #1565c0' : '1px solid #e0e0e0',
                background: selected?.id === s.id ? '#e3f2fd' : 'white',
                transition:'all 0.15s',
              }}
              onMouseEnter={e => { if (selected?.id !== s.id) e.currentTarget.style.background='#f8fbff'; }}
              onMouseLeave={e => { if (selected?.id !== s.id) e.currentTarget.style.background='white'; }}
            >
              {/* 제목 */}
              <div style={{fontWeight:'bold',fontSize:14,color:'#333',marginBottom:6,lineHeight:1.4}}>{s.title}</div>

              {/* 메타 정보 그리드 */}
              {(s.doc_id || s.program) && (
                <div style={{background:'#f8fbff',borderRadius:6,padding:'6px 10px',marginBottom:6,border:'1px solid #e3f2fd'}}>
                  {s.doc_id && (
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:2}}>
                      <span style={{fontSize:10,color:'#999',minWidth:45}}>문서ID</span>
                      <span style={{fontSize:11,color:'#1565c0',fontFamily:'monospace',fontWeight:'bold'}}>{s.doc_id}</span>
                    </div>
                  )}
                  {s.program && (
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:10,color:'#999',minWidth:45}}>프로그램</span>
                      <span style={{fontSize:11,color:'#2e7d32',fontFamily:'monospace',fontWeight:'bold'}}>{s.program}</span>
                    </div>
                  )}
                </div>
              )}

              {/* 뱃지 */}
              <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:4}}>
                {s.module && <span style={{fontSize:10,padding:'1px 8px',background:'#e3f2fd',color:'#1565c0',borderRadius:10,fontWeight:'bold'}}>{s.module}</span>}
                {s.dev_type && <span style={{fontSize:10,padding:'1px 8px',background:'#f3e8fd',color:'#7b1fa2',borderRadius:10}}>{s.dev_type}</span>}
              </div>

              {/* 테이블 + 날짜 */}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:4}}>
                <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  {(s.tables||'').split(',').map((t,i) => t.trim()).filter(Boolean).map((t,i) => (
                    <span key={i} style={{fontSize:10,padding:'1px 6px',background:'#e8f5e9',color:'#2e7d32',borderRadius:4,fontFamily:'monospace'}}>{t}</span>
                  ))}
                </div>
                <span style={{fontSize:10,color:'#bbb',whiteSpace:'nowrap',marginLeft:4}}>{s.created_at}</span>
              </div>

              {/* 삭제 */}
              <div style={{marginTop:8,textAlign:'right'}}>
                <button onClick={(e) => {e.stopPropagation(); handleDelete(s.id);}}
                  style={{padding:'2px 8px',background:'#ffebee',color:'#c62828',border:'1px solid #ef9a9a',borderRadius:4,cursor:'pointer',fontSize:10}}>삭제</button>
              </div>
            </div>
          ))}
        </div>

        {/* 상세 */}
        {selected && (
          <div>
            {/* 제목 편집 + 메타정보 */}
            <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',padding:16,marginBottom:16}}>
              <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:12}}>
                <input type="text" value={selected.title} onChange={e => setSelected(p => ({...p,title:e.target.value}))}
                  style={{flex:1,padding:10,fontSize:16,fontWeight:'bold',borderRadius:6,border:'1px solid #ccc'}} />
                <button onClick={handleSave} disabled={saving}
                  style={{padding:'10px 20px',background:'#1565c0',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:14,fontWeight:'bold'}}>
                  {saving ? '저장중...' : '저장'}
                </button>
                {msg && <span style={{fontSize:12,color:'#2e7d32'}}>{msg}</span>}
              </div>
              {/* 메타 정보 표시 */}
              {(selected.doc_id || selected.program || selected.dev_type || selected.module) && (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))',gap:8}}>
                  {[
                    {label:'문서ID', value:selected.doc_id, color:'#1565c0', bg:'#e3f2fd'},
                    {label:'프로그램명', value:selected.program, color:'#2e7d32', bg:'#e8f5e9'},
                    {label:'개발유형', value:selected.dev_type, color:'#7b1fa2', bg:'#f3e8fd'},
                    {label:'SAP 모듈', value:selected.module, color:'#e67700', bg:'#fef3e2'},
                  ].filter(m => m.value).map((m,i) => (
                    <div key={i} style={{padding:'8px 12px',background:m.bg,borderRadius:6,border:'1px solid '+m.bg}}>
                      <div style={{fontSize:10,color:'#999',marginBottom:2}}>{m.label}</div>
                      <div style={{fontSize:13,color:m.color,fontWeight:'bold',fontFamily:m.label==='프로그램명'||m.label==='문서ID'?'monospace':'inherit'}}>{m.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* FS/TS 탭 + 도구 */}
            <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',overflow:'hidden'}}>
              <div style={{display:'flex',borderBottom:'2px solid #e0e0e0'}}>
                {[{k:'fs',l:'Functional Spec (FS)',c:'#2e7d32',bg:'#e8f5e9'},{k:'ts',l:'Technical Spec (TS)',c:'#1565c0',bg:'#e3f2fd'}].map(t => (
                  <button key={t.k} onClick={() => setActiveTab(t.k)}
                    style={{flex:1,padding:14,border:'none',cursor:'pointer',fontSize:15,fontWeight:'bold',
                      background:activeTab===t.k?t.bg:'white',color:activeTab===t.k?t.c:'#999',
                      borderBottom:activeTab===t.k?'3px solid '+t.c:'3px solid transparent'}}>{t.l}</button>
                ))}
              </div>
              <div style={{padding:'10px 20px',background:'#fafafa',borderBottom:'1px solid #eee',display:'flex',justifyContent:'flex-end',gap:6}}>
                
                <button onClick={() => handleDownload("word")} style={{padding:"5px 12px",background:"#1565c0",color:"white",border:"none",borderRadius:4,cursor:"pointer",fontSize:12,fontWeight:"bold"}}>Word</button>
                <button onClick={() => handleDownload("excel")} style={{padding:"5px 12px",background:"#2e7d32",color:"white",border:"none",borderRadius:4,cursor:"pointer",fontSize:12,fontWeight:"bold"}}>Excel</button>
                <button onClick={openMail} style={{padding:'5px 12px',background:'#ea4335',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>메일</button>
                <button onClick={() => {var t=activeTab==='fs'?selected.fs_spec:selected.ts_spec;if(navigator.clipboard&&navigator.clipboard.writeText){navigator.clipboard.writeText(t)}else{var ta=document.createElement('textarea');ta.value=t;ta.style.position='fixed';ta.style.left='-9999px';document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta)};alert('복사됨');}}
                  style={{padding:'5px 12px',background:'#666',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>복사</button>
              </div>
              <div style={{padding:24,fontSize:14,lineHeight:1.8,minHeight:300}}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{activeTab === 'fs' ? selected.fs_spec : selected.ts_spec}</ReactMarkdown>
              </div>
              {/* AI 보정 */}
              <div style={{padding:'16px 20px',borderTop:'1px solid #eee',background:'#fafafa'}}>
                <div style={{fontSize:13,fontWeight:'bold',color:'#333',marginBottom:8}}>AI 보정</div>
                <div style={{display:'flex',gap:10}}>
                  <input type="text" value={refineInput} onChange={e => setRefineInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !refining) handleRefine(); }}
                    placeholder="보정 지시 (예: 컬럼 설명 더 상세하게, Selection Screen 파라미터 추가)"
                    style={{flex:1,padding:10,borderRadius:6,border:'1px solid #ccc',fontSize:14}} />
                  <button onClick={handleRefine} disabled={refining}
                    style={{padding:'10px 20px',background:refining?'#ccc':'#e67700',color:'white',border:'none',borderRadius:6,cursor:refining?'not-allowed':'pointer',fontSize:14,fontWeight:'bold',whiteSpace:'nowrap'}}>
                    {refining ? '보정중...' : 'AI 보정'}
                  </button>
                </div>
                <div style={{display:'flex',gap:6,marginTop:8,flexWrap:'wrap'}}>
                  {['테이블 설명을 더 상세하게','테스트 케이스 추가','에러 처리 보완','성능 최적화 내용 추가','한글 설명 보강'].map((q,i) => (
                    <button key={i} onClick={() => {setRefineInput(q); }}
                      style={{padding:'4px 10px',background:'#fff',border:'1px solid #ddd',borderRadius:12,cursor:'pointer',fontSize:11,color:'#666'}}>{q}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {!selected && specs.length > 0 && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',color:'#999',fontSize:15}}>
            왼쪽에서 스펙을 선택하세요
          </div>
        )}
      </div>

      {/* 메일 전송 모달 */}
      {showMail && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}
          onClick={e => { if (e.target === e.currentTarget) setShowMail(false); }}>
          <div style={{background:'white',borderRadius:12,width:'90%',maxWidth:500,overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            <div style={{background:'linear-gradient(135deg,#ea4335,#fb7160)',padding:'18px 24px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{color:'white',fontSize:17,fontWeight:'bold'}}>스펙정의서 메일 전송</span>
              <button onClick={() => setShowMail(false)} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',width:28,height:28,borderRadius:'50%',cursor:'pointer',fontSize:15}}>X</button>
            </div>
            <div style={{padding:22}}>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:13,fontWeight:'bold',display:'block',marginBottom:4}}>받는 사람</label>
                <input type="text" value={mailTo} onChange={e => setMailTo(e.target.value)}
                  placeholder="example@email.com (여러 명은 쉼표로 구분)"
                  style={{width:'100%',padding:10,borderRadius:6,border:'1px solid #ddd',fontSize:14,boxSizing:'border-box'}} />
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:13,fontWeight:'bold',display:'block',marginBottom:4}}>제목</label>
                <input type="text" value={mailSubject} onChange={e => setMailSubject(e.target.value)}
                  style={{width:'100%',padding:10,borderRadius:6,border:'1px solid #ddd',fontSize:14,boxSizing:'border-box'}} />
              </div>
              <div style={{background:'#f8f9fa',borderRadius:6,padding:12,marginBottom:14,maxHeight:150,overflowY:'auto',fontSize:12,border:'1px solid #eee',color:'#555'}}>
                <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                  {selected?.doc_id && <span style={{fontSize:10,padding:'1px 6px',background:'#e3f2fd',color:'#1565c0',borderRadius:8}}>{selected.doc_id}</span>}
                  {selected?.program && <span style={{fontSize:10,padding:'1px 6px',background:'#e8f5e9',color:'#2e7d32',borderRadius:8,fontFamily:'monospace'}}>{selected.program}</span>}
                </div>
                <div style={{padding:8,background:'#fff3e0',borderRadius:4,marginBottom:6,fontSize:11,color:'#e67700'}}>
                  <strong>첨부:</strong> {selected?.title}_FS_TS.docx (Word 문서)
                </div>
                <div style={{fontSize:11,color:'#888'}}>
                  메일 본문에는 AI가 생성한 <strong>간략 요약</strong>이 포함되고,<br/>
                  FS + TS 전체 내용은 <strong>Word 파일로 첨부</strong>됩니다.
                </div>
              </div>
              {mailResult && (
                <div style={{padding:10,borderRadius:6,marginBottom:12,fontSize:13,
                  background:mailResult.startsWith('success')?'#e8f5e9':'#ffebee',
                  color:mailResult.startsWith('success')?'#2e7d32':'#c62828',
                }}>{mailResult.split(':').slice(1).join(':')}</div>
              )}
              <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                <button onClick={() => setShowMail(false)} style={{padding:'9px 18px',background:'#f1f3f4',color:'#333',border:'none',borderRadius:6,cursor:'pointer',fontSize:14}}>취소</button>
                <button onClick={handleSendMail} disabled={mailSending}
                  style={{padding:'9px 22px',background:mailSending?'#ccc':'#ea4335',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:14,fontWeight:'bold'}}>
                  {mailSending ? '전송중...' : '메일 보내기'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SpecAgent;
